import React, { useState, useEffect } from 'react';
import { Card, CardBody, Row, Col, Table, Form } from 'react-bootstrap';
import { 
  useUpdatePermissionsMutation, 
  useGetAllPermissionMatrixDataQuery,
  useGetAllFeaturesQuery,
  useGetFeatureGroupsQuery,
} from '../../../redux/services/rolesPermissionService';
import ButtonComponentV2 from '../../../components/ButtonComponentV2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faSpinner, faRefresh, faCheckSquare, faSquare } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import './index.css';

// Define local interfaces for internal state management
interface LocalRole {
  id: string;
  name: string;
  description: string;
  permissions: {
    [featureId: string]: {
      list: boolean;
      view: boolean;
      edit: boolean;
      delete: boolean;
    };
  };
}

interface LocalFeature {
  id: string;
  name: string;
  groupId: string;
  groupName: string;
  description: string;
}

interface LocalFeatureGroup {
  id: string;
  name: string;
  description: string;
}

interface LocalAction {
  id: string;
  name: string;
  description: string;
}

const PermissionsMatrix = () => {
  // Local state for managing data from APIs
  const [roles, setRoles] = useState<LocalRole[]>([]);
  const [featureGroups, setFeatureGroups] = useState<LocalFeatureGroup[]>([]);
  const [features, setFeatures] = useState<LocalFeature[]>([]);const [actions] = useState<LocalAction[]>([
  { id: 'list', name: 'list', description: 'View list of items' },
  { id: 'view', name: 'view', description: 'View item details' },
  { id: 'edit', name: 'edit', description: 'Modify items' },
  { id: 'delete', name: 'delete', description: 'Remove items' }
]);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalPermissions, setOriginalPermissions] = useState<any>({});
  const [isLoadingData, setIsLoadingData] = useState(false);

  // RTK Query hooks
  const [updatePermissions, { isLoading: isSaving }] = useUpdatePermissionsMutation();
  const { data: permissionMatrixData, isLoading: isLoadingMatrix, refetch: refetchMatrix } = useGetAllPermissionMatrixDataQuery();
  const { data: featuresData, refetch: refetchFeatures } = useGetAllFeaturesQuery();
  const { data: featureGroupsData, refetch: refetchFeatureGroups } = useGetFeatureGroupsQuery();

  // Load all data from APIs
  const loadAllData = async () => {
    setIsLoadingData(true);
    try {
      // Load feature groups, features, and roles
      const [featureGroupsResult, featuresResult] = await Promise.all([
        Promise.resolve(featureGroupsData),
        Promise.resolve(featuresData)
      ]);

      // Transform and set feature groups
      if (featureGroupsResult?.featureGroups) {
        const transformedFeatureGroups: LocalFeatureGroup[] = featureGroupsResult.featureGroups.map((group: any) => ({
          id: group.id,
          name: group.displayName || group.name || 'Unknown Group',
          description: group.description || ''
        }));
        setFeatureGroups(transformedFeatureGroups);
      }

      // Transform and set features with group names
      if (featuresResult) {
        const transformedFeatures: LocalFeature[] = featuresResult.map((feature: any) => {
          const featureGroup = featureGroupsResult?.featureGroups?.find((group: any) => group.id === feature.featureGroupId);
          return {
            id: feature.id,
            name: feature.displayName,
            groupId: feature.featureGroupId || 'default',
            groupName: featureGroup?.displayName || featureGroup?.name || 'Uncategorized',
            description: feature.description || ''
          };
        });
        setFeatures(transformedFeatures);
      }

      // Transform and set roles with permissions from matrix data
      if (permissionMatrixData) {
        const transformedRoles: LocalRole[] = permissionMatrixData.map((role: any) => ({
          id: role.id,
          name: role.displayName,
          description: role.description,
          // Deep copy permissions to avoid read-only issues
          permissions: JSON.parse(JSON.stringify(role.permissions || {}))
        }));
        setRoles(transformedRoles);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load permission matrix data');
    } finally {
      setIsLoadingData(false);
    }
  };

  // Helper functions for managing permissions
  const hasPermission = (roleId: string, featureId: string, actionName: string): boolean => {
    const role = roles.find(r => r.id === roleId);
    return role?.permissions?.[featureId]?.[actionName as keyof typeof role.permissions[string]] || false;
  };

  const updateRolePermission = (roleId: string, featureId: string, actionName: string, hasPermission: boolean) => {
    setRoles(prevRoles => 
      prevRoles.map(role => {
        if (role.id === roleId) {
          // Deep copy the permissions object to avoid read-only issues
          const updatedPermissions = JSON.parse(JSON.stringify(role.permissions || {}));
          
          if (!updatedPermissions[featureId]) {
            updatedPermissions[featureId] = {
              list: false,
              view: false,
              edit: false,
              delete: false
            };
          }
          
          // Create a new feature permissions object to ensure it's not read-only
          updatedPermissions[featureId] = {
            ...updatedPermissions[featureId],
            [actionName]: hasPermission
          };
          
          return { 
            ...role, 
            permissions: updatedPermissions 
          };
        }
        return role;
      })
    );
  };

  // Group features by feature group for better organization within each role
  const getFeaturesByGroup = (groupId: string): LocalFeature[] => {
    return features.filter(feature => feature.groupId === groupId);
  };

  // Check if all permissions in a group are selected for a specific action
  const isGroupActionAllSelected = (roleId: string, groupId: string, actionName: string): boolean => {
    const groupFeatures = getFeaturesByGroup(groupId);
    if (groupFeatures.length === 0) return false;
    return groupFeatures.every(feature => hasPermission(roleId, feature.id, actionName));
  };

  // Check if some (but not all) permissions in a group are selected for a specific action
  const isGroupActionIndeterminate = (roleId: string, groupId: string, actionName: string): boolean => {
    const groupFeatures = getFeaturesByGroup(groupId);
    if (groupFeatures.length === 0) return false;
    const selectedCount = groupFeatures.filter(feature => hasPermission(roleId, feature.id, actionName)).length;
    return selectedCount > 0 && selectedCount < groupFeatures.length;
  };

  // Toggle all permissions in a group for a specific action
  const toggleGroupAction = (roleId: string, groupId: string, actionName: string) => {
    const groupFeatures = getFeaturesByGroup(groupId);
    const isAllSelected = isGroupActionAllSelected(roleId, groupId, actionName);
    
    groupFeatures.forEach(feature => {
      updateRolePermission(roleId, feature.id, actionName, !isAllSelected);
    });
    
    setHasUnsavedChanges(true);
  };

  // Check if all permissions in a group are selected (all actions for all features)
  const isGroupAllSelected = (roleId: string, groupId: string): boolean => {
    const groupFeatures = getFeaturesByGroup(groupId);
    if (groupFeatures.length === 0) return false;
    
    return groupFeatures.every(feature => 
      actions.every(action => hasPermission(roleId, feature.id, action.name))
    );
  };

  // Check if some (but not all) permissions in a group are selected
  // const isGroupIndeterminate = (roleId: string, groupId: string): boolean => {
  //   const groupFeatures = getFeaturesByGroup(groupId);
  //   if (groupFeatures.length === 0) return false;
    
  //   const totalPermissions = groupFeatures.length * actions.length;
  //   const selectedPermissions = groupFeatures.reduce((count, feature) => 
  //     count + actions.filter(action => hasPermission(roleId, feature.id, action.name)).length, 0
  //   );
    
  //   return selectedPermissions > 0 && selectedPermissions < totalPermissions;
  // };

  // Toggle all permissions in a group (all actions for all features)
  const toggleGroupAll = (roleId: string, groupId: string) => {
    const groupFeatures = getFeaturesByGroup(groupId);
    const isAllSelected = isGroupAllSelected(roleId, groupId);
    
    groupFeatures.forEach(feature => {
      actions.forEach(action => {
        updateRolePermission(roleId, feature.id, action.name, !isAllSelected);
      });
    });
    
    setHasUnsavedChanges(true);
  };

  const resetRolePermissions = () => {
    setRoles(prevRoles => 
      prevRoles.map(role => ({
        ...role,
        // Deep copy the original permissions to avoid read-only issues
        permissions: JSON.parse(JSON.stringify(originalPermissions[role.id] || {}))
      }))
    );
  };

  const handleRefreshData = async () => {
    await Promise.all([refetchMatrix(), refetchFeatures(), refetchFeatureGroups()]);
    loadAllData();
  };

  useEffect(() => {
    // Store original permissions for comparison with deep copy
    const permissions: any = {};
    roles.forEach(role => {
      permissions[role.id] = JSON.parse(JSON.stringify(role.permissions || {}));
    });
    setOriginalPermissions(permissions);
  }, [roles]);

  const handlePermissionChange = (roleId: string, featureId: string, actionName: string, hasPermissionValue: boolean) => {
    updateRolePermission(roleId, featureId, actionName, hasPermissionValue);
    setHasUnsavedChanges(true);
  };

  const handleSaveChanges = async () => {
    try {
      // Transform data to match the required API format
      const requestData: {
        roles: {
          id: string;
          permissions: {
            [featureKey: string]: {
              [actionName: string]: boolean;
            };
          };
        }[];
      } = {
        roles: []
      };

      roles.forEach(role => {
        const roleData = {
          id: role.id,
          permissions: {} as { [featureKey: string]: { [actionName: string]: boolean } }
        };

        // Initialize all features with all actions set to false
        features.forEach(feature => {
          const featureKey = feature.id;
          roleData.permissions[featureKey] = {};
          
          // Set all actions to false by default
          actions.forEach(action => {
            roleData.permissions[featureKey][action.name] = false;
          });
        });

        // Now override with actual permissions (set to true where selected)
        Object.keys(role.permissions || {}).forEach(featureId => {
          const featurePermissions = role.permissions[featureId];
          
          if (featurePermissions && roleData.permissions[featureId]) {
            actions.forEach(action => {
              if (featurePermissions[action.name as keyof typeof featurePermissions] !== undefined) {
                roleData.permissions[featureId][action.name] = featurePermissions[action.name as keyof typeof featurePermissions];
              }
            });
          }
        });

        // Only add roles that have at least one permission set to true
        const hasAnyPermissions = Object.values(roleData.permissions).some(featurePerms =>
          Object.values(featurePerms).some(permission => permission === true)
        );

        if (hasAnyPermissions) {
          // Clean up: only include features that have at least one true permission
          const cleanedPermissions: { [featureKey: string]: { [actionName: string]: boolean } } = {};
          Object.keys(roleData.permissions).forEach(featureId => {
            const featurePerms = roleData.permissions[featureId];
            const hasFeaturePermissions = Object.values(featurePerms).some(permission => permission === true);
            if (hasFeaturePermissions) {
              cleanedPermissions[featureId] = featurePerms;
            }
          });
          
          requestData.roles.push({
            id: role.id,
            permissions: cleanedPermissions
          });
        }
      });
      
      // Use RTK Query mutation to update permissions
      await updatePermissions(requestData).unwrap();
      
      // Update original permissions to current state
      const permissions: any = {};
      roles.forEach(role => {
        permissions[role.id] = { ...role.permissions };
      });
      setOriginalPermissions(permissions);
      setHasUnsavedChanges(false);
      
      toast.success('Permissions saved successfully!');
    } catch (error) {
      console.error('Error saving permissions:', error);
      toast.error('Failed to save permissions. Please try again.');
    }
  };

  const handleDiscardChanges = () => {
    // Reset permissions to original state
    resetRolePermissions();
    setHasUnsavedChanges(false);
    toast.info('Changes discarded successfully');
  };

  const getActionColor = (actionName: string) => {
    switch (actionName.toLowerCase()) {
      case 'list': return 'primary';
      case 'view': return 'success';
      case 'edit': return 'warning';
      case 'delete': return 'danger';
      default: return 'secondary';
    }
  };

  // Load data when component mounts or when permission matrix data changes
  useEffect(() => {
    if (permissionMatrixData) {
      loadAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionMatrixData, featuresData, featureGroupsData]);

  // Get total permissions count for a role
  const getTotalPermissionsForRole = (roleId: string): number => {
    return features.reduce((count, feature) => {
      return count + actions.filter(action => 
        hasPermission(roleId, feature.id, action.name)
      ).length;
    }, 0);
  };

  return (
    <div>
      <Row className="px-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4>Role-Based Permissions Matrix</h4>
              <p className="text-muted">Manage permissions for each role by assigning feature access rights</p>
            </div>
            <div className="d-flex gap-2">
              <ButtonComponentV2
                titel="Refresh Data"
                icon={<FontAwesomeIcon icon={faRefresh} spin={isLoadingData || isLoadingMatrix} />}
                onClick={handleRefreshData}
                color="#fff"
                backgroundColor="#6c757d"
                size="medium"
                isDisabled={isLoadingData || isLoadingMatrix}
              />
              {hasUnsavedChanges && (
                <>
                  <ButtonComponentV2
                    titel="Discard Changes"
                    onClick={handleDiscardChanges}
                    color="#fff"
                    backgroundColor="#6c757d"
                    size="medium"
                  />
                  <ButtonComponentV2
                    titel={isSaving ? "Saving..." : "Save Changes"}
                    icon={<FontAwesomeIcon icon={isSaving ? faSpinner : faSave} spin={isSaving} />}
                    onClick={handleSaveChanges}
                    color="#fff"
                    backgroundColor="#590B21"
                    size="medium"
                    isDisabled={isSaving}
                  />
                </>
              )}
            </div>
          </div>
        </Col>
      </Row>
      
      {(isLoadingData || isLoadingMatrix) && (
        <Row>
          <Col>
            <Card>
              <CardBody className="text-center py-5">
                <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-primary mb-3" />
                <h5 className="text-muted">Loading Permission Matrix Data...</h5>
                <p className="text-muted">Please wait while we fetch the latest permissions data.</p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Role-Centric Permissions Matrix */}
      {!(isLoadingData || isLoadingMatrix) && roles.map(role => (
        <Row key={role.id} className="mb-0">
          <Col>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div className='d-flex align-items-center'>
                    <h4><span className="fx-4 badge bg-success me-2">{role.description}</span></h4>
                    <h5 className="mb-1">
                      <span className="text-small text-muted me-2">{role.name}</span>
                      <small className="text-muted">({getTotalPermissionsForRole(role.id)} feature {getTotalPermissionsForRole(role.id) === 1 ? 'group' : 'groups'} assigned)</small>
                    </h5>
                  </div>
                </div>

                {/* Group features by feature groups within each role */}
                {featureGroups.filter(group => getFeaturesByGroup(group.id).length > 0).map(group => {
                  const groupFeatures = getFeaturesByGroup(group.id);
                  
                  return (
                    <div key={group.id} className="mb-4">
                      <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
                        <h6 className="text-secondary mb-0">
                          <i className="fas fa-folder me-2"></i>
                          {group.name}
                          <small className="text-muted ms-2">({groupFeatures.length} {groupFeatures.length === 1 ? 'feature' : 'features'})</small>
                        </h6>
                        
                        {/* Group Select All Controls */}
                        <div className="d-flex align-items-center gap-2">
                          <small className="text-muted me-2">Group Controls:</small>
                          
                          {/* Select All Group Button */}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary d-flex align-items-center"
                            onClick={() => toggleGroupAll(role.id, group.id)}
                            title={isGroupAllSelected(role.id, group.id) ? "Deselect All" : "Select All"}
                          >
                            <FontAwesomeIcon 
                              icon={isGroupAllSelected(role.id, group.id) ? faCheckSquare : faSquare} 
                              className="me-1" 
                            />
                            <span className="d-none d-sm-inline">
                              {isGroupAllSelected(role.id, group.id) ? "Deselect All" : "Select All"}
                            </span>
                          </button>
                        </div>
                      </div>

                      <div className="table-responsive">
                        <Table striped bordered hover size="sm" className="mb-0">
                          <thead className="permission-matrix-table-header">
                            <tr>
                              <th style={{ width: '300px' }}>Feature</th>
                              {actions.map(action => (
                                <th key={action.id} className="text-center" style={{ width: '120px' }}>
                                  <div className="d-flex flex-column align-items-center">
                                    <span className={`badge bg-${getActionColor(action.name)} mb-1`}>
                                      {action.name}
                                    </span>
                                    <div className="small mb-2">{action.description}</div>
                                    
                                    {/* Column Select All Checkbox */}
                                    <Form.Check
                                      type="checkbox"
                                      checked={isGroupActionAllSelected(role.id, group.id, action.name)}
                                      ref={(input:any) => {
                                        if (input) {
                                          input.indeterminate = isGroupActionIndeterminate(role.id, group.id, action.name);
                                        }
                                      }}
                                      onChange={() => toggleGroupAction(role.id, group.id, action.name)}
                                      className="form-check-sm"
                                      title={`${isGroupActionAllSelected(role.id, group.id, action.name) ? 'Deselect' : 'Select'} all ${action.name} permissions in this group`}
                                    />
                                    <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                      Select All
                                    </small>
                                  </div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {groupFeatures.map(feature => (
                              <tr key={feature.id}>
                                <td className="align-middle">
                                  <div className="fw-bold">{feature.name}</div>
                                  <div className="small text-muted">{feature.description}</div>
                                </td>
                                {actions.map(action => (
                                  <td key={action.id} className="text-center align-middle">
                                    <Form.Check
                                      type="checkbox"
                                      checked={hasPermission(role.id, feature.id, action.name)}
                                      onChange={(e) => handlePermissionChange(
                                        role.id, 
                                        feature.id, 
                                        action.name, 
                                        e.target.checked
                                      )}
                                      className="form-check-lg"
                                    />
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  );
                })}

                {/* Show message if no features are available */}
                {features.length === 0 && (
                  <div className="alert alert-info">
                    No features available. Please create features first in the Features tab.
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      ))}

      {/* Show message if no roles are available */}
      {!(isLoadingData || isLoadingMatrix) && roles.length === 0 && (
        <Row>
          <Col>
            <Card>
              <CardBody className="text-center py-5">
                <h5 className="text-muted">No Roles Available</h5>
                <p className="text-muted">
                  Please create roles first in the Roles tab to manage permissions.
                </p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Permission Summary */}
      {!(isLoadingData || isLoadingMatrix) && roles.length > 0 && (
        <Row>
          <Col>
            <Card>
              <CardBody>
                <h5>Permission Summary</h5>
                <div className="row">
                  {roles.map(role => {
                    const totalPermissions = getTotalPermissionsForRole(role.id);
                    const maxPossiblePermissions = features.length * actions.length;
                    const permissionPercentage = maxPossiblePermissions > 0 ? Math.round((totalPermissions / maxPossiblePermissions) * 100) : 0;
                    
                    return (
                      <div key={role.id} className="col-md-3 mb-3">
                        <div className="card border-primary">
                          <div className="card-body text-center">
                            <h6 className="card-title">{role.name}</h6>
                            <h4 className="text-primary">{totalPermissions}</h4>
                            <small className="text-muted">
                              {totalPermissions} of {maxPossiblePermissions} permissions ({permissionPercentage}%)
                            </small>
                            <div className="progress mt-2" style={{ height: '4px' }}>
                              <div 
                                className="progress-bar bg-primary" 
                                style={{ width: `${permissionPercentage}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default PermissionsMatrix;
